<template>
  <router-link
    :to="{
      name: 'product-details',
      params: { id: product.id }
    }"
    class="product-card"
  >
    <template v-if="isGallery">
      <img :src="image" v-if="image" />

      <div
        class="image d-flex align-items-center justify-content-center bg-neutral-076"
        v-else
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="35"
          height="50"
          viewBox="0 0 35 50"
          fill="none"
        >
          <path
            d="M8.07936 15.0873L23.1667 0H0.5V15.0873V22.6667V49.0873L34.5 15.0873H8.07936Z"
            fill="#FCFCFD"
          />
        </svg>
      </div>
    </template>

    <div
      class="list-item"
      :class="isGallery ? 'gallery-display' : 'list-display'"
    >
      <div class="details">
        <img :src="image" alt="product-image" v-if="image" />
        <div
          class="image d-flex align-items-center justify-content-center bg-neutral-076"
          v-else
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="40"
            viewBox="0 0 35 50"
            fill="none"
          >
            <path
              d="M8.07936 15.0873L23.1667 0H0.5V15.0873V22.6667V49.0873L34.5 15.0873H8.07936Z"
              fill="#FCFCFD"
            />
          </svg>
        </div>

        <div class="d-flex flex-column gap-2">
          <div>
            {{ product.name }}
          </div>
          <div class="functional-2 text-neutral-400 product-code">
            {{ product.code }}
          </div>
        </div>
      </div>

      <bp-tooltip
        class="status"
        text="Clique aqui para solicitar ao suporte a aprovação do seu produto."
        :disabled="!inReview"
      >
        <span
          :class="formatStatusClass(product.status)"
          class="d-flex align-items-center gap-2"
        >
          {{ formatStatus(product.status) }}
          <bp-icon
            v-if="inReview"
            size="14"
            icon="fa-light fa-circle-exclamation"
          />
        </span>

        <template #button>
          <bp-button
            size="sm"
            variant="glyph"
            label="Solicitar aprovação"
            class="text-primary-200 mt-2"
            trailing-icon="fa-light fa-arrow-up-right-from-square"
            :disabled="!inReview"
            @click="requestApproval"
          />
        </template>
      </bp-tooltip>

      <div class="functional-2 text-neutral-500 product-type">
        {{ formatProductType(product.type_product) }}
      </div>

      <div class="functional-2 text-neutral-400 created-at">
        Criado em {{ formatDate(product.created_at) }}
      </div>

      <div class="links">
        <bp-button
          size="sm"
          class="fw-4"
          variant="glyph"
          icon="fa-light fa-link"
          label="Links de divulgação"
          @click="openLinks"
        />
      </div>

      <bp-dropdown
        class="dropdown"
        :actions="actions"
        :variant="isGallery ? 'filled-2' : 'glyph'"
        @links="openLinks"
        @duplicate="duplicate"
        @deactivate="changeStatus('inactive')"
      />
    </div>
  </router-link>
</template>

<script>
import { approvalMessageUrl } from "@/functions/product";
import ProductActionsMixin from "@/mixins/productActions.js";

export default {
  mixins: [ProductActionsMixin],
  props: {
    isGallery: Boolean,
    product: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    image() {
      return this.product?.picture || null;
    },
    actions() {
      const actions = [
        {
          text: "Duplicar produto",
          icon: "fa-light fa-copy",
          event: "duplicate"
        },
        {
          text: "Links de divulgação",
          icon: "fa-light fa-link",
          event: "links"
        }
      ];

      if (this.product.status == "active") {
        actions.push({
          text: "Desativar produto",
          class: "text-negative-600",
          icon: "fa-light fa-power-off",
          confirmation: {
            text:
              "Tem certeza que deseja desativar este produto? Ao confirmar este produto não estará mais disponível para compra até que você o reative.",
            variant: "negative",
            buttonText: "Cancelar",
            secondaryButtonText: "Desativar",
            icon: "fa fa-hexagon-exclamation",
            secondary: true
          },
          event: "deactivate"
        });
      } else if (this.product.status == "inactive") {
        actions.push({
          text: "Ativar produto",
          class: "text-positive-600",
          icon: "fa-light fa-power-off",
          confirmation: {
            icon: "fa fa-hexagon-exclamation",
            variant: "alert",
            buttonText: "Entendido",
            text:
              "Entre em contato com o suporte para solicitar a ativação do produto."
          }
        });
      }

      return actions;
    },
    inReview() {
      return !!["in_review", "draft"].includes(this.product.status);
    }
  },
  methods: {
    formatStatus(status) {
      return {
        active: "Ativo",
        inactive: "Inativo",
        draft: "Pendente",
        in_review: "Em revisão",
        reject: "Rejeitado"
      }[status];
    },
    formatStatusClass(status) {
      return {
        active: "active-badge",
        inactive: "inactive-badge",
        draft: "draft-badge",
        in_review: "draft-badge",
        reject: "inactive-badge"
      }[status];
    },
    formatProductType(type_product) {
      return {
        online_course: "Curso Online",
        signature: "Assinatura",
        onsite_event: "Evento presencial"
      }[type_product];
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString("pt-BR");
    },

    requestApproval() {
      window.open(approvalMessageUrl(this.product, this.$user.user), "_blank");
    },
    duplicate() {
      this.$store.dispatch("product/duplicate", this.product.id).then(() => {
        this.$message.success("Produto duplicado!");
        this.$emit("refresh");
      });
    },
    openLinks() {
      this.$router.push({
        name: "product-links",
        params: { id: this.product.id }
      });
    },
    changeStatus(status) {
      this.$store
        .dispatch("product/put", { id: this.product.id, status })
        .then(() => {
          this.$message.success(
            `O Produto foi ${status == "active" ? "ativado" : "desativado"}!`
          );
          this.$emit("refresh");
        })
        .catch(() => {
          this.$message.error("Erro ao atualizar status do produto");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.product-card {
  display: block;
  text-align: left;
  position: relative;
  color: $neutral-700;
  text-decoration: none;

  height: 100%;
  border-radius: 4px;
  border: 1px solid var(--Semantic-Neutral-100, #d7d7d7);
  background: var(--Surface-Cumulative-plus4, rgba(255, 255, 255, 0.16));
  transition: 0.2s all;
  &:hover {
    border-color: $neutral-076;
    box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.12);

    .dropdown {
      opacity: 1 !important;
    }
  }

  & > img,
  .image {
    width: 100%;
    height: 185px;
    object-fit: cover;
    border-radius: 3px 3px 0 0;
  }

  .list-item {
    display: grid;
    padding: 12px;

    .details {
      order: 1;
      gap: 16px;
      display: flex;
      align-items: center;

      img,
      .image {
        width: 72px;
        height: 72px;
        object-fit: cover;
        border-radius: 4px;
      }
    }
    .status {
      order: 2;
    }
    .product-type {
      order: 3;
    }
    .created-at {
      order: 4;
    }
    .links {
      order: 5;
    }
    .dropdown {
      order: 6;
    }

    &.gallery-display {
      gap: 4px;
      padding-top: 4px;

      .details {
        img,
        .image,
        .product-code {
          display: none !important;
        }
      }

      .dropdown {
        top: 8px;
        right: 8px;
        position: absolute;
        transition: 0.2s opacity;

        @media screen and (min-width: 992px) {
          &:has(span[style*="display: none"]) {
            opacity: 0;
          }
        }
      }
    }

    &.list-display {
      gap: 16px;
      align-items: center;
      grid-template-columns: 2fr 0.5fr 0.7fr 0.6fr 0.7fr auto;

      .details {
        order: 1;
      }
      .product-type {
        order: 2;
      }
      .links {
        order: 3;
      }
      .created-at {
        order: 4;
      }
      .status {
        order: 5;
        margin-left: auto;
      }

      @media screen and (max-width: 991px) {
        gap: 8px;
        grid-template-columns: 1fr auto auto;

        .details {
          gap: 8px;
        }
        .product-type,
        .links,
        .created-at {
          display: none;
        }
      }
    }
  }
}
</style>
